import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { 
  Loader2, 
  CheckCircle, 
  Clock, 
  Bell, 
  BellRing, 
  ShoppingCart, 
  CreditCard as PaymentIcon, 
  XCircle, 
  Users, 
  Timer, 
  Smartphone, 
  Mail,
  Receipt,
  BarChart3,
  Gift,
  Megaphone,
  TrendingUp,
  Calendar
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface NotificationSettingsProps {
  merchantId: string;
  canEdit: boolean;
  merchantEmail?: string | null;
}

interface NotificationPreferences {
  id?: string;
  // Order Related
  notify_new_order: boolean;
  notify_payment_received: boolean;
  notify_order_completed: boolean;
  notify_order_expired: boolean;
  notify_refund_requested: boolean;
  notify_low_participation: boolean;
  // Abandoned Cart
  abandoned_cart_reminder_enabled: boolean;
  abandoned_cart_first_reminder_hours: number;
  abandoned_cart_second_reminder_enabled: boolean;
  abandoned_cart_second_reminder_hours: number;
  // Delivery Settings
  digest_mode: 'instant' | 'daily' | 'weekly';
  digest_time: string;
  notification_email: string | null;
  notification_phone: string | null;
  // Analytics (stored in report_preferences but we'll manage here for UX)
  weekly_reports: boolean;
  monthly_reports: boolean;
  annual_reports: boolean;
  // Promotions & Offers
  promotions_enabled: boolean;
  product_updates: boolean;
  tips_and_best_practices: boolean;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function NotificationSettings({ 
  merchantId, 
  canEdit, 
  merchantEmail,
}: NotificationSettingsProps) {
  const customersLabel = "Customers";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; user_id: string; role: string; created_at: string }>>([]);
  const [invitations, setInvitations] = useState<Array<{ id: string; email: string; role: string; status: string; created_at: string; expires_at: string }>>([]);
  
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    notify_new_order: true,
    notify_payment_received: true,
    notify_order_completed: true,
    notify_order_expired: true,
    notify_refund_requested: true,
    notify_low_participation: false,
    abandoned_cart_reminder_enabled: true,
    abandoned_cart_first_reminder_hours: 24,
    abandoned_cart_second_reminder_enabled: false,
    abandoned_cart_second_reminder_hours: 48,
    digest_mode: 'instant',
    digest_time: '09:00',
    notification_email: null,
    notification_phone: null,
    weekly_reports: true,
    monthly_reports: true,
    annual_reports: false,
    promotions_enabled: true,
    product_updates: true,
    tips_and_best_practices: true,
  });

  useEffect(() => {
    fetchData();
  }, [merchantId]);

  const fetchData = async () => {
    try {
      // Fetch notification preferences
      const { data: notifData, error: notifError } = await supabase
        .from("merchant_notification_preferences")
        .select("*")
        .eq("merchant_id", merchantId)
        .maybeSingle();

      // Fetch report preferences for analytics settings
      const { data: reportData } = await supabase
        .from("merchant_report_preferences")
        .select("*")
        .eq("merchant_id", merchantId)
        .maybeSingle();

      if (!notifError && notifData) {
        setPreferences(prev => ({
          ...prev,
          id: notifData.id,
          notify_new_order: notifData.notify_new_order,
          notify_payment_received: notifData.notify_payment_received,
          notify_order_completed: notifData.notify_order_completed,
          notify_order_expired: notifData.notify_order_expired,
          notify_refund_requested: notifData.notify_refund_requested,
          notify_low_participation: notifData.notify_low_participation,
          abandoned_cart_reminder_enabled: notifData.abandoned_cart_reminder_enabled ?? true,
          abandoned_cart_first_reminder_hours: notifData.abandoned_cart_first_reminder_hours ?? 24,
          abandoned_cart_second_reminder_enabled: notifData.abandoned_cart_second_reminder_enabled ?? false,
          abandoned_cart_second_reminder_hours: notifData.abandoned_cart_second_reminder_hours ?? 48,
          digest_mode: notifData.digest_mode as 'instant' | 'daily' | 'weekly',
          digest_time: notifData.digest_time?.slice(0, 5) || '09:00',
          notification_email: notifData.notification_email,
          notification_phone: notifData.notification_phone,
        }));
      }

      if (reportData) {
        setPreferences(prev => ({
          ...prev,
          weekly_reports: reportData.report_frequency === 'weekly' && reportData.email_reports_enabled,
          monthly_reports: reportData.report_frequency === 'monthly' && reportData.email_reports_enabled,
        }));
      }

      // Fetch team data for recipients display
      const { data: members } = await supabase
        .from('merchant_users')
        .select('id, user_id, role, created_at')
        .eq('merchant_id', merchantId);

      const { data: invites } = await supabase
        .from('merchant_invitations')
        .select('id, email, role, status, created_at, expires_at')
        .eq('merchant_id', merchantId)
        .eq('status', 'pending');

      setTeamMembers(members || []);
      setInvitations(invites || []);
    } catch (error) {
      console.error("Error fetching notification data:", error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const payload = {
        merchant_id: merchantId,
        notify_new_order: preferences.notify_new_order,
        notify_payment_received: preferences.notify_payment_received,
        notify_order_completed: preferences.notify_order_completed,
        notify_order_expired: preferences.notify_order_expired,
        notify_refund_requested: preferences.notify_refund_requested,
        notify_low_participation: preferences.notify_low_participation,
        abandoned_cart_reminder_enabled: preferences.abandoned_cart_reminder_enabled,
        abandoned_cart_first_reminder_hours: preferences.abandoned_cart_first_reminder_hours,
        abandoned_cart_second_reminder_enabled: preferences.abandoned_cart_second_reminder_enabled,
        abandoned_cart_second_reminder_hours: preferences.abandoned_cart_second_reminder_hours,
        digest_mode: preferences.digest_mode,
        digest_time: preferences.digest_time + ':00',
        notification_email: preferences.notification_email || null,
        notification_phone: preferences.notification_phone || null,
        whatsapp_enabled: false,
      } as any;

      if (preferences.id) {
        const { error } = await supabase
          .from("merchant_notification_preferences")
          .update(payload)
          .eq("id", preferences.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("merchant_notification_preferences")
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        setPreferences(prev => ({ ...prev, id: data.id }));
      }

      toast.success("Notification settings saved!");
    } catch (error: any) {
      console.error("Error saving notification preferences:", error);
      toast.error("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Merchant Notifications
          </CardTitle>
          <CardDescription>
            Configure alerts and communications you receive from Shakatah
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* ===== ORDER RELATED ===== */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-lg">Order Related</h3>
            </div>
            <p className="text-sm text-muted-foreground -mt-2">
              Alerts about split payment orders, payments, and status changes
            </p>
            
            <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <BellRing className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">New order created</Label>
                  </div>
                  <Switch
                    checked={preferences.notify_new_order}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, notify_new_order: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <PaymentIcon className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">Payment received</Label>
                  </div>
                  <Switch
                    checked={preferences.notify_payment_received}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, notify_payment_received: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <Label className="text-sm">Order completed</Label>
                  </div>
                  <Switch
                    checked={preferences.notify_order_completed}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, notify_order_completed: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-destructive" />
                    <Label className="text-sm">Order expired</Label>
                  </div>
                  <Switch
                    checked={preferences.notify_order_expired}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, notify_order_expired: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">Refund requested</Label>
                  </div>
                  <Switch
                    checked={preferences.notify_refund_requested}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, notify_refund_requested: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <Timer className="h-4 w-4 text-amber-600" />
                    <Label className="text-sm">Low participation warning</Label>
                  </div>
                  <Switch
                    checked={preferences.notify_low_participation}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, notify_low_participation: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Low participation alerts are sent 24 hours before deadline when participation is below 50%
              </p>
            </div>
          </div>

          
            <Separator />
              <Separator />
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-lg">Abandoned Cart Reminders</h3>
                </div>
                <p className="text-sm text-muted-foreground -mt-2">
                  Automatically remind logged-in customers who add items to their split cart but don't check out
                </p>
                
                <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <Label className="text-sm">Enable abandoned cart reminders</Label>
                        <p className="text-xs text-muted-foreground">Send email reminders to customers with abandoned split carts</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.abandoned_cart_reminder_enabled}
                      onCheckedChange={(checked) => 
                        setPreferences(prev => ({ ...prev, abandoned_cart_reminder_enabled: checked }))
                      }
                      disabled={!canEdit}
                    />
                  </div>

                  {preferences.abandoned_cart_reminder_enabled && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm text-muted-foreground">First reminder after</Label>
                          <Select
                            value={String(preferences.abandoned_cart_first_reminder_hours)}
                            onValueChange={(value) => 
                              setPreferences(prev => ({ ...prev, abandoned_cart_first_reminder_hours: Number(value) }))
                            }
                            disabled={!canEdit}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="12">12 hours</SelectItem>
                              <SelectItem value="24">24 hours</SelectItem>
                              <SelectItem value="48">48 hours</SelectItem>
                              <SelectItem value="72">72 hours</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border bg-background h-fit">
                          <div>
                            <Label className="text-sm">Second reminder</Label>
                            <p className="text-xs text-muted-foreground">Send a follow-up if still not checked out</p>
                          </div>
                          <Switch
                            checked={preferences.abandoned_cart_second_reminder_enabled}
                            onCheckedChange={(checked) => 
                              setPreferences(prev => ({ ...prev, abandoned_cart_second_reminder_enabled: checked }))
                            }
                            disabled={!canEdit}
                          />
                        </div>
                      </div>

                      {preferences.abandoned_cart_second_reminder_enabled && (
                        <div className="space-y-2">
                          <Label className="text-sm text-muted-foreground">Second reminder after</Label>
                          <Select
                            value={String(preferences.abandoned_cart_second_reminder_hours)}
                            onValueChange={(value) => 
                              setPreferences(prev => ({ ...prev, abandoned_cart_second_reminder_hours: Number(value) }))
                            }
                            disabled={!canEdit}
                          >
                            <SelectTrigger className="w-full md:w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="36">36 hours</SelectItem>
                              <SelectItem value="48">48 hours</SelectItem>
                              <SelectItem value="72">72 hours</SelectItem>
                              <SelectItem value="96">96 hours</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </>
                  )}
                  
                  <p className="text-xs text-muted-foreground">
                    Reminders include product details, images, and a direct link to continue checkout
                  </p>
                </div>
              </div>

          <Separator />

          {/* ===== ANALYTICS REPORTS ===== */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-lg">Analytics & Reports</h3>
            </div>
            <p className="text-sm text-muted-foreground -mt-2">
              Periodic performance summaries and business insights
            </p>
            
            <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">Weekly summary</Label>
                  </div>
                  <Switch
                    checked={preferences.weekly_reports}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, weekly_reports: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">Monthly report</Label>
                  </div>
                  <Switch
                    checked={preferences.monthly_reports}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, monthly_reports: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">Annual review</Label>
                  </div>
                  <Switch
                    checked={preferences.annual_reports}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, annual_reports: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Reports include order volume, revenue trends, participant insights, and conversion metrics
              </p>
            </div>
          </div>

          <Separator />

          {/* ===== PROMOTIONS & OFFERS ===== */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-lg">Promotions & Offers</h3>
            </div>
            <p className="text-sm text-muted-foreground -mt-2">
              Platform updates, special offers, and helpful resources from Shakatah
            </p>
            
            <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <Gift className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">Special offers</Label>
                  </div>
                  <Switch
                    checked={preferences.promotions_enabled}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, promotions_enabled: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <BellRing className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">Product updates</Label>
                  </div>
                  <Switch
                    checked={preferences.product_updates}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, product_updates: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 rounded-lg border bg-background">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm">Tips & best practices</Label>
                  </div>
                  <Switch
                    checked={preferences.tips_and_best_practices}
                    onCheckedChange={(checked) => 
                      setPreferences(prev => ({ ...prev, tips_and_best_practices: checked }))
                    }
                    disabled={!canEdit}
                  />
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Stay informed about new features, promotional campaigns, and tips to grow your split payment conversions
              </p>
            </div>
          </div>

          <Separator />

          {/* ===== DELIVERY SETTINGS ===== */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-lg">Delivery Settings</h3>
            </div>
            <p className="text-sm text-muted-foreground -mt-2">
              How and where you receive notifications
            </p>
            
            <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Delivery Mode</Label>
                  <Select
                    value={preferences.digest_mode}
                    onValueChange={(value: 'instant' | 'daily' | 'weekly') => 
                      setPreferences(prev => ({ ...prev, digest_mode: value }))
                    }
                    disabled={!canEdit}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="instant">Instant (real-time)</SelectItem>
                      <SelectItem value="daily">Daily digest</SelectItem>
                      <SelectItem value="weekly">Weekly digest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {preferences.digest_mode !== 'instant' && (
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Digest Time</Label>
                    <Input
                      type="time"
                      value={preferences.digest_time}
                      onChange={(e) => setPreferences(prev => ({ ...prev, digest_time: e.target.value }))}
                      disabled={!canEdit}
                    />
                  </div>
                )}
              </div>

              {/* Notification Recipients */}
              <div className="space-y-3 pt-3 border-t">
                <Label className="text-sm font-medium">Who receives alerts</Label>
                
                {/* Admin email - always receives */}
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                  <Mail className="h-4 w-4 text-green-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-700 dark:text-green-400 truncate">
                      {merchantEmail || "Store admin email"}
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-500">Primary admin</p>
                  </div>
                  <Badge variant="outline" className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700 shrink-0">
                    Always
                  </Badge>
                </div>
                
                {/* Active Team members */}
                {teamMembers.filter(m => m.role !== 'super_admin').length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Active team members:</p>
                    {teamMembers.filter(m => m.role !== 'super_admin').map((member) => (
                      <div key={member.id} className="flex items-center gap-3 p-3 rounded-lg border bg-green-50/50 dark:bg-green-950/20 border-green-200/50 dark:border-green-800/50">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">Team Member</p>
                          <p className="text-xs text-muted-foreground capitalize">{member.role.replace('_', ' ')}</p>
                        </div>
                        <Badge variant="secondary" className="shrink-0 capitalize bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700">
                          Active
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Pending invitations */}
                {invitations.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Pending acceptance:</p>
                    {invitations.map((invitation) => (
                      <div key={invitation.id} className="flex items-center gap-3 p-3 rounded-lg border bg-amber-50/50 dark:bg-amber-950/20 border-amber-200/50 dark:border-amber-800/50">
                        <Clock className="h-4 w-4 text-amber-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{invitation.email}</p>
                          <p className="text-xs text-muted-foreground capitalize">{invitation.role.replace('_', ' ')} • Awaiting acceptance</p>
                        </div>
                        <Badge variant="outline" className="shrink-0 bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700">
                          Pending
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                {teamMembers.filter(m => m.role !== 'super_admin').length === 0 && invitations.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">No team members added yet</p>
                )}
                
                {/* Additional email */}
                <div className="space-y-2 pt-3 border-t">
                  <Label className="text-sm text-muted-foreground">Additional notification email (optional)</Label>
                  <Input
                    type="email"
                    placeholder="e.g., manager@company.com"
                    value={preferences.notification_email || ''}
                    onChange={(e) => setPreferences(prev => ({ ...prev, notification_email: e.target.value || null }))}
                    disabled={!canEdit}
                  />
                  <p className="text-xs text-muted-foreground">Add an extra email to receive all alerts</p>
                </div>
              </div>

            </div>
          </div>

          {/* Save Button */}
          <Button 
            onClick={savePreferences} 
            disabled={saving || !canEdit} 
            className="w-full sm:w-auto"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Notification Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
